/**
 * Pure requirement resolver.
 *
 * Contract: docs/source-of-truth/worker-service-delivery-architecture.md
 * ("Requirement decision engine").
 *
 * Takes canonical facts about one occurrence and returns the resolved
 * requirement set plus a gate outcome. It performs no I/O, reads no clock and
 * makes no network call: the caller supplies `evaluatedAt`, so the same inputs
 * always produce the same output and a decision can be replayed from an audit
 * record.
 *
 * Everything this returns is advisory to the caller in one direction only — a
 * server RPC must re-run it at the point of use rather than trusting a cached
 * result, because credentials and delegations expire between assignment and
 * arrival.
 */

import type { RequirementAuthority } from './authority';
import { isTenantPolicyRequirementCode, ownerAuthorityForRequirementCode } from './composition';
import type { GateStage, RequirementDecision, ResolvedRequirement, GateOutcome } from './decisions';
import { evaluateGate, resolveRequirement } from './decisions';
import type { ExecutionMode } from './execution-modes';
import { canRosterWorkerOccurrence } from './execution-modes';
import type { ServicePackCode } from './packs';
import { requiresExplicitDelegation } from './packs';
import { bindingForServiceCode, isModeAllowedForService } from './registry';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * A credential, competency or delegation the worker holds.
 *
 * `expiresAt` is compared against `evaluatedAt`, never against a clock read
 * inside this module.
 */
export interface HeldEvidence {
  code: string;
  /** ISO instant. Absent means it does not expire. */
  expiresAt?: string;
  /** False when the record exists but did not pass, e.g. a failed check. */
  satisfied: boolean;
}

export interface ResolverFacts {
  tenantId: string;
  occurrenceTenantId: string;
  clientId: string;
  inclusionClientId: string;
  workerId: string | null;
  assignedWorkerId: string | null;

  serviceCode: string;
  executionMode: ExecutionMode;

  inclusionActive: boolean;
  serviceCancelled: boolean;

  workerOnLeave: boolean;
  hasOverlappingAssignment: boolean;
  clientNoGoForWorker: boolean;

  /** Credentials, competencies and delegations the worker holds. */
  heldEvidence: readonly HeldEvidence[];
  /** Requirement codes this pack/overlay set demands of the worker. */
  requiredEvidenceCodes: readonly string[];
  /** Codes whose authority is provider policy rather than professional scope. */
  providerPolicyEvidenceCodes?: readonly string[];
  /**
   * Exact-slot, audited exceptions keyed by requirement code. Only honoured for
   * provider-policy requirements; composition rejects the rest at publication.
   */
  acceptedExceptions?: Readonly<Record<string, string>>;

  /** ISO instant supplied by the caller. */
  evaluatedAt: string;
}

export interface ResolverResult {
  requirements: readonly ResolvedRequirement[];
  outcome: GateOutcome;
  pack: ServicePackCode | null;
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

function req(
  code: string,
  authority: RequirementAuthority,
  stage: GateStage,
  decision: RequirementDecision,
  reasonCode: string,
  facts: ResolverFacts,
  exceptionId?: string,
): ResolvedRequirement {
  return resolveRequirement({
    code,
    authority,
    stage,
    decision,
    reasonCode,
    sourceType: 'resolver',
    effectiveAt: facts.evaluatedAt,
    evaluatedAt: facts.evaluatedAt,
    ...(exceptionId ? { exceptionId } : {}),
  });
}

/**
 * Compares two instants by value rather than by string.
 *
 * A raw ISO string comparison is format-sensitive: `2026-07-28T09:00:00Z` and
 * `2026-07-28T09:00:00.000Z` are the same moment and sort differently, and an
 * offset such as `+10:00` sorts as though it were UTC. Returns null when either
 * side is unparseable, so the caller can fail closed instead of guessing.
 */
function isExpiredAt(expiresAt: string, evaluatedAt: string): boolean | null {
  const expiry = Date.parse(expiresAt);
  const now = Date.parse(evaluatedAt);
  if (Number.isNaN(expiry) || Number.isNaN(now)) return null;
  return expiry <= now;
}

/**
 * Classifies the evidence a worker holds for one requirement code.
 *
 * Distinguishes the ways evidence can be unusable, because collapsing them
 * would tell a coordinator "not allowed" when the truth is "expired yesterday"
 * or "nobody ever recorded it".
 *
 * A worker may hold several records for the same code — a renewed certificate
 * alongside its lapsed predecessor. The answer is therefore "is there a valid
 * one", not "what does the first row say", so the result does not depend on the
 * order the records arrive in. When none is valid, the most informative failure
 * is reported: an unparseable date is `UNAVAILABLE` (we could not tell), a
 * lapsed record is `EXPIRED`, an unsatisfied one is `FAILED`.
 */
function classifyEvidence(
  code: string,
  held: readonly HeldEvidence[],
  evaluatedAt: string,
): RequirementDecision {
  const records = held.filter((e) => e.code === code);
  if (records.length === 0) return 'MISSING';

  let sawUnparseable = false;
  let sawExpired = false;
  let sawUnsatisfied = false;

  for (const record of records) {
    if (!record.satisfied) {
      sawUnsatisfied = true;
      continue;
    }
    if (!record.expiresAt) return 'PASS';
    const expired = isExpiredAt(record.expiresAt, evaluatedAt);
    if (expired === null) {
      sawUnparseable = true;
      continue;
    }
    if (!expired) return 'PASS';
    sawExpired = true;
  }

  if (sawUnparseable) return 'UNAVAILABLE';
  if (sawExpired) return 'EXPIRED';
  if (sawUnsatisfied) return 'FAILED';
  return 'MISSING';
}

/**
 * Resolves every applicable requirement for one occurrence at one stage.
 *
 * Identity checks come first and are `STATUTORY`, so a tenant, client or
 * assignment mismatch blocks with no exception path — matching the override
 * taxonomy, where identity mismatch is the one row that can never be overridden.
 */
export function resolveRequirements(facts: ResolverFacts, stage: GateStage): ResolverResult {
  const out: ResolvedRequirement[] = [];
  const binding = bindingForServiceCode(facts.serviceCode);

  // --- Identity and tenancy ------------------------------------------------
  out.push(
    req(
      'TENANT_MATCHES_OCCURRENCE',
      'STATUTORY',
      'PLAN_READY',
      facts.tenantId === facts.occurrenceTenantId ? 'PASS' : 'FAILED',
      'tenant_mismatch',
      facts,
    ),
  );
  out.push(
    req(
      'CLIENT_MATCHES_INCLUSION',
      'STATUTORY',
      'PLAN_READY',
      facts.clientId === facts.inclusionClientId ? 'PASS' : 'FAILED',
      'client_mismatch',
      facts,
    ),
  );

  // --- Service identity ----------------------------------------------------
  // An unknown or delisted code fails closed. There is no generic pack.
  out.push(
    req(
      'SERVICE_CODE_PUBLISHED',
      'PROGRAM',
      'PLAN_READY',
      binding ? 'PASS' : 'FAILED',
      binding ? 'service_published' : 'service_code_unknown_or_delisted',
      facts,
    ),
  );
  out.push(
    req(
      'EXECUTION_MODE_ALLOWED_FOR_SERVICE',
      'PROGRAM',
      'PLAN_READY',
      isModeAllowedForService(facts.serviceCode, facts.executionMode) ? 'PASS' : 'FAILED',
      'mode_not_allowed_for_service',
      facts,
    ),
  );
  out.push(
    req(
      'CARE_PLAN_INCLUSION_ACTIVE',
      'PROGRAM',
      'PLAN_READY',
      facts.inclusionActive ? 'PASS' : 'FAILED',
      'inclusion_not_active',
      facts,
    ),
  );
  out.push(
    req(
      'SERVICE_NOT_CANCELLED',
      'PROGRAM',
      'SCHEDULE',
      facts.serviceCancelled ? 'FAILED' : 'PASS',
      'service_cancelled',
      facts,
    ),
  );

  // --- Assignment ----------------------------------------------------------
  // Only meaningful for modes that produce rostered worker time. For a voucher
  // arrangement or an expense there is no worker to match, and asserting a
  // worker requirement would fabricate one.
  if (canRosterWorkerOccurrence(facts.executionMode)) {
    const matched =
      facts.workerId !== null &&
      facts.assignedWorkerId !== null &&
      facts.workerId === facts.assignedWorkerId;
    out.push(
      req(
        'WORKER_MATCHES_ASSIGNMENT',
        'STATUTORY',
        'ASSIGN',
        matched ? 'PASS' : 'FAILED',
        'worker_not_assigned_to_occurrence',
        facts,
      ),
    );
    out.push(
      req(
        'WORKER_NOT_ON_LEAVE',
        'PROGRAM',
        'ASSIGN',
        facts.workerOnLeave ? 'FAILED' : 'PASS',
        'worker_on_leave',
        facts,
      ),
    );
    out.push(
      req(
        'NO_OVERLAPPING_ASSIGNMENT',
        'PROGRAM',
        'ASSIGN',
        facts.hasOverlappingAssignment ? 'FAILED' : 'PASS',
        'overlapping_assignment',
        facts,
      ),
    );
    out.push(
      req(
        'CLIENT_NO_GO_ABSENT',
        'PROGRAM',
        'ASSIGN',
        facts.clientNoGoForWorker ? 'FAILED' : 'PASS',
        'client_no_go_for_worker',
        facts,
      ),
    );
  } else {
    out.push(
      req(
        'WORKER_MATCHES_ASSIGNMENT',
        'STATUTORY',
        'ASSIGN',
        'NOT_APPLICABLE_BY_DESIGN',
        'mode_has_no_rostered_worker',
        facts,
      ),
    );
  }

  // --- Worker evidence -----------------------------------------------------
  const policyCodes = new Set(facts.providerPolicyEvidenceCodes ?? []);
  const exceptions = facts.acceptedExceptions ?? {};

  for (const code of facts.requiredEvidenceCodes) {
    // Authority is re-derived, never asserted by the caller.
    //
    // An owned code takes its owner outright. Everything else defaults to
    // PROFESSIONAL_SCOPE, and drops to PROVIDER_POLICY only when the code is on
    // the registered tenant-policy allowlist *and* the caller's policy set
    // agrees. Both conditions are needed: the allowlist stops an unclassified
    // credential becoming waivable by omission, and the caller's set still
    // decides whether this particular tenant applies it.
    const owner = ownerAuthorityForRequirementCode(code);
    const tenantOwned = isTenantPolicyRequirementCode(code) && policyCodes.has(code);
    const authority: RequirementAuthority =
      owner ?? (tenantOwned ? 'PROVIDER_POLICY' : 'PROFESSIONAL_SCOPE');
    const raw = classifyEvidence(code, facts.heldEvidence, facts.evaluatedAt);

    // An exception can only rescue a provider-policy gap, and only one that is
    // actually deficient. Applying it to a passing requirement would leave a
    // spurious exception on the audit record.
    const exceptionId = exceptions[code];
    if (raw !== 'PASS' && exceptionId && authority === 'PROVIDER_POLICY') {
      out.push(
        req(
          code,
          authority,
          'START',
          'EXCEPTION_ACCEPTED',
          'exception_accepted',
          facts,
          exceptionId,
        ),
      );
      continue;
    }
    out.push(req(code, authority, 'START', raw, `evidence_${raw.toLowerCase()}`, facts));
  }

  // Delegated packs need an explicit delegation record. Inheriting the
  // delegator's registration is precisely what the design forbids.
  if (binding && requiresExplicitDelegation(binding.basePack)) {
    const decision = classifyEvidence('DELEGATION_CURRENT', facts.heldEvidence, facts.evaluatedAt);
    out.push(
      req(
        'DELEGATION_CURRENT',
        'PROFESSIONAL_SCOPE',
        'START',
        decision,
        `delegation_${decision.toLowerCase()}`,
        facts,
      ),
    );
  }

  return {
    requirements: out,
    outcome: evaluateGate(stage, out),
    pack: binding?.basePack ?? null,
  };
}
